import { useEffect, useRef, useState } from 'react';
import { useCreateMachine } from 'fsm';
import { getParamDefaultsFromSteps } from '../../stepRenderer';
import { getLowLevelAuthFlowMachineDefinition } from '../components/LowLevelAuthFlowView/LowLevelAuthFlowMachineDefinition';
import { LowLevelAuthFlowMachineContext, useLowLevelAuthFlowMachine } from '../components/LowLevelAuthFlowView/LowLevelAuthFlowMachineContext';
import { useFinishAuth } from '../components/LowLevelAuthFlowView/hooks/useFinishAuth';
import { reportHeadlessAuthError } from './reportHeadlessAuthError';
import { jsx as _jsx } from "react/jsx-runtime";
function HeadlessAuthFlow({
  appName,
  oAuthWindow,
  onError,
  onCancel
}) {
  const {
    state
  } = useLowLevelAuthFlowMachine();
  const {
    startAuth
  } = useFinishAuth();
  const startedRef = useRef(false);
  const reportedRef = useRef(false);
  const {
    authError
  } = state.context;
  useEffect(() => {
    // This component only mounts once the machine is running, so it is safe to
    // dispatch the auth events immediately.
    if (!startedRef.current) {
      startedRef.current = true;
      startAuth(oAuthWindow);
    }
    if (authError && !reportedRef.current) {
      reportedRef.current = true;
      // OAuthWindowClosed means the user deliberately dismissed the popup — not
      // an error worth surfacing. Still call onCancel so the trigger resets.
      if (authError.type !== 'OAuthWindowClosed') {
        reportHeadlessAuthError(authError, appName, onError);
      }
      // Headless has no modal to keep open, so end the flow on failure. This
      // resets the trigger's open state so a re-click restarts a fresh attempt;
      // unmounting also closes the popup via UnifiedHeadlessAuthView (its owner).
      onCancel === null || onCancel === void 0 || onCancel();
    }
  }, [authError, appName, oAuthWindow, onError, onCancel, startAuth]);
  return null;
}
/**
 * Runs an OAuth auth flow with no UI. It mounts the auth-flow machine, starts
 * the OAuth handshake once on mount (reusing the popup opened on the gesture),
 * and renders nothing. Failures are surfaced via `onError`.
 */
export function HeadlessAuthDriver({
  portalId,
  authConfig,
  onComplete,
  onError,
  onCancel,
  oAuthWindow
}) {
  const machineDefinition = getLowLevelAuthFlowMachineDefinition();
  const machine = useCreateMachine(machineDefinition, {
    initialContext: {
      portalId,
      authConfig,
      params: getParamDefaultsFromSteps(authConfig.authFlow.steps),
      stepIndex: -1,
      initialStepIndex: -1,
      authState: {
        status: 'pending'
      },
      authError: undefined,
      onComplete,
      authProcessStatus: 'idle',
      pollingCount: 0,
      oAuthState: '',
      oAuthWindow: null
    }
  });

  // The machine starts asynchronously and dispatching events before it is
  // running drops them, so wait until it is running to mount the flow. This
  // effect is declared after useCreateMachine, so it runs after the machine's
  // own start effect on the same commit.
  const [machineReady, setMachineReady] = useState(false);
  useEffect(() => {
    if (!machineReady && machine.state.isRunning()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMachineReady(true);
    }
  }, [machine, machineReady]);
  return /*#__PURE__*/_jsx(LowLevelAuthFlowMachineContext.Provider, {
    value: machine,
    children: machineReady && /*#__PURE__*/_jsx(HeadlessAuthFlow, {
      appName: authConfig.appName,
      oAuthWindow: oAuthWindow,
      onError: onError,
      onCancel: onCancel
    })
  });
}